import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useBranding } from './hooks/useBranding';
import { LoginPage } from './pages/LoginPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { InitializePage } from './pages/InitializePage';
import { DashboardPage } from './pages/DashboardPage';
import { PendingApprovalPage } from './pages/PendingApprovalPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AdminMaterialsPage } from './pages/AdminMaterialsPage';
import { QuotesPage } from './pages/QuotesPage';
import { CreateQuotePage } from './pages/CreateQuotePage';
import { EditQuotePage } from './pages/EditQuotePage';
import { QuoteDetailPage } from './pages/QuoteDetailPage';
import { AdminProcessesPage } from './pages/AdminProcessesPage';
import { AdminLaborRatesPage } from './pages/AdminLaborRatesPage';
import { AdminVinylsPage } from './pages/AdminVinylsPage';
import { AdminSettingsPage } from './pages/AdminSettingsPage';
import { MaterialCatalogPage } from './pages/MaterialCatalogPage';
import { ClientsPage } from './pages/ClientsPage';
import { AdminInitializePage } from './pages/AdminInitializePage';
import { DebugAuthPage } from './pages/DebugAuthPage';
import { TestFirestorePage } from './pages/TestFirestorePage';
import './styles/branding.css';
import './App.css';

function AppContent() {
  // Load branding on app startup
  useBranding();

  return (
    <Routes>
      <Route path="/debug-auth" element={<DebugAuthPage />} />
      <Route path="/test-firestore" element={<TestFirestorePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/initialize" element={<InitializePage />} />

      <Route
        path="/verify-email"
        element={
          <ProtectedRoute requireApproval={false}>
            <VerifyEmailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/pending-approval"
        element={
          <ProtectedRoute>
            <PendingApprovalPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/materials"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminMaterialsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/quotes"
        element={
          <ProtectedRoute>
            <QuotesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/quotes/new"
        element={
          <ProtectedRoute>
            <CreateQuotePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/quotes/:projectId/edit"
        element={
          <ProtectedRoute>
            <EditQuotePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/quotes/:projectId"
        element={
          <ProtectedRoute>
            <QuoteDetailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <ClientsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/processes"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminProcessesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/labor-rates"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminLaborRatesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/vinyls"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminVinylsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminSettingsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/initialize"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminInitializePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/catalog"
        element={
          <ProtectedRoute>
            <MaterialCatalogPage />
          </ProtectedRoute>
        }
      />

      {/* Redirect root to dashboard or login */}
      <Route
        path="/"
        element={<Navigate to="/dashboard" replace />}
      />

      {/* Catch all other routes */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrandingProvider>
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </BrandingProvider>
  );
}

export default App;
